/**
 * Everything the sidebar decides, kept out of the markup.
 *
 * The sidebar now carries the whole of the app chrome. The top bar is gone, so
 * the brand switcher, the person signed in, the palette's face, the theme
 * control and the brand's colour all live in this one column, and each of those
 * came with a small rule about when it shows and what it says. Rules like that
 * belong somewhere they can be covered, and this workspace has no DOM test
 * environment, so they are here rather than inside a component (the same
 * arrangement `SystemBanner.model.ts` has with its strips).
 *
 * Nothing in this file imports React or touches `window`. The one function that
 * has to know what machine it is on takes the machine as an argument.
 */

// ---------------------------------------------------------------------------
// Badges on a nav item
// ---------------------------------------------------------------------------

/**
 * Which live number feeds a nav item's badge.
 *
 * A key rather than a number on the item, because `NAV_GROUPS` is a constant
 * read at module load and these counts arrive from the server long afterwards.
 * The list says which number it wants, the sidebar looks it up.
 */
export type NavBadgeKey = 'queue' | 'approvals' | 'inboxUnread' | 'inboxAction' | ProspectingBadgeKey

/** The counts the chrome knows how to draw. All optional: absent means not loaded yet. */
export interface NavCounts {
  queue?: number
  approvals?: number
  inboxUnread?: number
  /** Threads the inbox rail lists under Action required, its `needsAttention`. */
  inboxAction?: number
  /** The Prospecting rows' three, read through the gateway at /api/v1/prospecting (`useProspectingCounts`). */
  prospectingReview?: number
  prospectingWaiting?: number
  prospectingProspects?: number
}

/**
 * The three numbers, picked off the three bodies that carry them.
 *
 * Separate from the hook so the choice of field can be covered, because that
 * choice is the whole of the risk. The inbox counts body carries `total`,
 * `unread`, `needsAttention` and more, all numbers, so reading the wrong one
 * type-checks cleanly and draws a badge that is plausible and wrong. Unread is
 * the owner's call: the badge is the Unread tab's own figure, so reading a
 * thread takes one off it and the number means "somebody spoke and nobody here
 * has looked".
 *
 * Each source is optional because each request lands on its own schedule, and
 * an absent one stays absent rather than becoming a zero.
 */
export function navCountsFrom(sources: {
  cards?: { total: number } | undefined
  approvals?: { total: number } | undefined
  inbox?: { unread: number; needsAttention?: number } | undefined
}): NavCounts {
  return {
    queue: sources.cards?.total,
    approvals: sources.approvals?.total,
    inboxUnread: sources.inbox?.unread,
    inboxAction: sources.inbox?.needsAttention,
  }
}

/**
 * `solid` is the filled brand circle Salesforge puts on its Primebox row ahead
 * of the outlined count, and that the inbox rail puts on Action required. It is
 * drawn by the sidebar itself rather than by `Badge`, which has no filled tone.
 */
export type NavBadgeTone = 'neutral' | 'accent' | 'solid'

export interface NavBadge {
  /** What the pill says, already clamped. */
  text: string
  tone: NavBadgeTone
  /** The sentence behind it, for a title attribute and for the collapsed rail. */
  title: string
}

/**
 * Where a count stops being a number and becomes "a lot".
 *
 * The pill sits in a 280px (17.5rem) column beside a label that already truncates, so a
 * five figure count would either push the label out or wrap the row. Ninety
 * nine is the last count that is still a count to somebody: past it the answer
 * to "how many" stops changing what you do next, which is to open the screen.
 */
export const BADGE_CAP = 99

export function countLabel(count: number): string {
  if (!Number.isFinite(count) || count <= 0) return '0'
  const whole = Math.floor(count)
  return whole > BADGE_CAP ? `${BADGE_CAP}+` : String(whole)
}

const BADGE_COPY: Record<NavBadgeKey, { tone: NavBadgeTone; one: string; many: string }> = {
  // Neutral. Cards are the day's work rather than an exception, and a warning
  // tone on the thing you do all day teaches people to ignore the colour.
  queue: { tone: 'neutral', one: 'card waiting to be actioned', many: 'cards waiting to be actioned' },
  // Accent, and the only one. This queue is the one place where nothing moves
  // until a person looks, and it also holds the sends that failed. Accent
  // rather than warning since the Salesforge pass, which marks a count that
  // needs action in the brand colour and keeps warning for things going wrong.
  approvals: { tone: 'accent', one: 'draft waiting for you', many: 'drafts waiting for you' },
  /*
   * Unread threads, which is exactly what `/inbox/counts` answers as `unread`
   * and exactly what the Unread tab on the page counts.
   *
   * This row used to wear every positive thread in the brand. The read behind
   * it asked `/conversations` for `handled=false`, the route's query schema has
   * no such field and zod strips what it does not declare, so the number never
   * went down however much of the inbox somebody worked through. Unread does go
   * down, one thread at a time, as the page marks them read.
   *
   * Neutral, like the queue. A reply arriving is the ordinary business of an
   * inbox rather than an exception.
   */
  inboxUnread: { tone: 'neutral', one: 'unread thread', many: 'unread threads' },
  /*
   * The rail's Action required figure, drawn in front of the unread count as
   * the reference's Primebox row draws its two numbers: a filled circle for
   * the threads waiting on a person, then the outlined count. The same body
   * and the same field the rail reads, so the two cannot disagree.
   */
  inboxAction: { tone: 'solid', one: 'thread needs action', many: 'threads need action' },
  /*
   * The Prospecting rows' three, the counts the old app's own column wore
   * (2026-10-21): what waits for your yes, posts waiting for a reply, and
   * prospects ready. The same tones that column used.
   */
  prospectingReview: { tone: 'accent', one: 'item waiting for your yes', many: 'items waiting for your yes' },
  prospectingWaiting: { tone: 'accent', one: 'post waiting for a reply', many: 'posts waiting for a reply' },
  prospectingProspects: { tone: 'neutral', one: 'prospect ready', many: 'prospects ready' },
}

/**
 * The badge for an item, or nothing.
 *
 * Zero draws nothing, which is the point of a badge: a row wearing a grey 0 is
 * a row that has taught you to stop reading it. A count that has not arrived
 * yet also draws nothing rather than a skeleton, because the alternative is the
 * whole nav shimmering on every load for three numbers nobody is waiting on.
 */
export function navBadge(key: NavBadgeKey | undefined, counts: NavCounts): NavBadge | null {
  if (key === undefined) return null
  const count = counts[key]
  if (count === undefined || !Number.isFinite(count) || count <= 0) return null
  const copy = BADGE_COPY[key]
  const whole = Math.floor(count)
  return {
    text: countLabel(whole),
    tone: copy.tone,
    title: `${whole} ${whole === 1 ? copy.one : copy.many}`,
  }
}

// ---------------------------------------------------------------------------
// Prospecting: the prospects pages, part of this app since 2026-10-24
// ---------------------------------------------------------------------------

/**
 * The prospects pages, at /prospecting (docs/PROSPECTING-MERGE.md).
 *
 * They were another app's until 2026-10-24, served at /prospector by a service
 * of its own, so these rows were full-page links into it and were lit only
 * inside it, where that app drew this same column in a frame. They are routes
 * of this app now, so the rows are router links like every other row, lit by
 * the path this router is on, with no page load between them.
 *
 * Who sees them: admins (an owner there), and anybody GET /me says has a seat.
 * The service refuses everybody else by itself, and the pages' own gate says
 * so in a sentence, so hiding the rows is tidiness, not the protection.
 *
 * A SEPARATE LIST FROM `NAV_GROUPS`, still. The rows have sub-rows under the
 * one that is open, a glyph set of their own and an audience that is not a
 * role, none of which a `NavItem` carries, and the column draws them between
 * two runs of the groups (`groupsAroundProspecting`).
 */
export type ProspectingGlyph = 'review' | 'queue' | 'posts' | 'audience' | 'prospects' | 'trends' | 'results' | 'keywords'

export type ProspectingBadgeKey = 'prospectingReview' | 'prospectingWaiting' | 'prospectingProspects'

export interface ProspectingLink {
  href: string
  label: string
  glyph: ProspectingGlyph
  hint: string
  /** The count the row wears, when it has one. */
  badge?: ProspectingBadgeKey
  /** The pages under the row, drawn beneath it while one of them is open. */
  children?: readonly { href: string; label: string }[]
}

/**
 * The heading over the rows, which is the activity and not a product name.
 * The owner asked for "Prospecting" on 2026-10-05, so the column reads as
 * the things you do (Threads, Discovery, Outreach, Prospecting) and then what
 * they stand on. Discovery joined above Outreach on 2026-10-26.
 */
export const PROSPECTING_HEADING = 'Prospecting'

/*
 * SIX ROWS, AND SETTINGS IS NOT ONE OF THEM.
 *
 * Posts is not one of them either since 2026-10-25 (the owner: "merge them into
 * one sidebar option and display only one of them"). A brand's ideas are drafts
 * on the Publish page, beside its written posts and videos, so the Outreach
 * column's Publish row is the one place for posts. /prospecting/posts still
 * answers for a brand with no workspace brand and for the ideas' settings.
 *
 * The Queue is first, and since 2026-10-24 it is the Review page too (the
 * owner: "we need to merge both of them"): its cards are List, and what Review
 * was is One at a time, at /prospecting?mode=review, where /prospecting/review
 * lands. Its number is everything waiting for a yes: the reply ideas and the
 * campaign messages. The folded rail's one glyph opens the first row, so it
 * opens the Queue.
 *
 * The settings are a tab of the Settings page, `/settings/prospecting` in
 * `settings-tabs-model.ts`, so the column carries one row called Settings, the
 * one at its foot (owner, 2026-10-05).
 */
export const PROSPECTING_LINKS: readonly ProspectingLink[] = [
  {
    href: '/prospecting',
    label: 'Queue',
    glyph: 'queue',
    hint: 'Posts worth a reply with comment ideas to edit and book, and campaign messages waiting for your yes',
    badge: 'prospectingReview',
  },
  {
    href: '/prospecting/creators',
    label: 'Audience',
    glyph: 'audience',
    hint: 'Creators, mentions and competitors',
    children: [
      { href: '/prospecting/creators', label: 'Creators' },
      { href: '/prospecting/mentions', label: 'Mentions' },
      { href: '/prospecting/competitors', label: 'Competitors' },
      { href: '/prospecting/reviews', label: 'Reviews' },
    ],
  },
  {
    href: '/prospecting/prospects',
    label: 'Prospects',
    glyph: 'prospects',
    hint: 'People likely to buy, waiting for approval',
    badge: 'prospectingProspects',
    children: [
      { href: '/prospecting/prospects', label: 'People found' },
      { href: '/prospecting/sequences', label: 'Sequences, all brands' },
    ],
  },
  { href: '/prospecting/trends', label: 'Trends', glyph: 'trends', hint: 'What people search for and talk about today' },
  { href: '/prospecting/results', label: 'Results', glyph: 'results', hint: 'Replies sent, clicks and what worked' },
  { href: '/prospecting/keywords', label: 'Keywords', glyph: 'keywords', hint: 'The searches that run for each brand and how each one does' },
]

/**
 * The prospects pages no row draws, offered by name in the command palette.
 * One at a time is a mode of the Queue, the two searches are linked from
 * nowhere else in the app (the old pages had no link to them either), and the
 * browser helper's setup is a page under Settings > Prospecting. Without these
 * the palette was the one place they could have been found, and it did not
 * know them. `settings` puts a destination in the settings group rather than
 * under the column heading.
 */
export interface ProspectingDestination {
  href: string
  label: string
  glyph: ProspectingGlyph
  hint: string
  settings?: boolean
}

export const PROSPECTING_UNLISTED: readonly ProspectingDestination[] = [
  { href: '/prospecting?mode=review', label: 'One at a time', glyph: 'review', hint: 'The Queue one post at a time, the next opening as you answer' },
  { href: '/prospecting/x-search', label: 'X search', glyph: 'keywords', hint: "What the phone found searching X for today's trends" },
  { href: '/prospecting/linkedin-search', label: 'LinkedIn search', glyph: 'keywords', hint: "What the phone found searching LinkedIn for today's trends" },
  {
    href: '/settings/prospecting/helper',
    label: 'Browser helper',
    glyph: 'review',
    hint: 'Install the Chrome extension and see what it found this week',
    settings: true,
  },
]

function trimmed(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
}

/**
 * Is this row the page open now? The Queue is the prospects pages' root, so it
 * is lit there (List and One at a time alike) and on the Review page's old
 * address, and nowhere else under it. Any other row is lit on its own page,
 * on a page under it, and on any of its sub-rows' pages.
 */
export function prospectingLinkLit(link: Pick<ProspectingLink, 'href' | 'children'>, pathname: string): boolean {
  const path = trimmed(pathname)
  if (link.href === '/prospecting') return path === '/prospecting' || path === '/prospecting/review'
  return [link.href, ...(link.children ?? []).map((c) => c.href)].some((x) => path === x || path.startsWith(`${x}/`))
}

/** Is this sub-row the page open now? */
export function prospectingChildLit(href: string, pathname: string): boolean {
  const path = trimmed(pathname)
  return path === href || path.startsWith(`${href}/`)
}

/** Whether any Prospecting row is lit, for the folded rail's one glyph. */
export function anyProspectingLit(pathname: string, links: readonly ProspectingLink[] = PROSPECTING_LINKS): boolean {
  return links.some((link) => prospectingLinkLit(link, pathname))
}

/**
 * The Prospecting rows this person sees: all of them for an admin or anybody
 * with a seat, none for anybody else. `seat` comes from GET /me
 * (`useProspectingSeat`), and an admin's rows never wait on it.
 */
export function prospectingLinksFor(isAdmin: boolean, seat = false): readonly ProspectingLink[] {
  return isAdmin || seat ? PROSPECTING_LINKS : []
}

/**
 * The group the Prospecting rows are drawn directly above.
 *
 * The owner asked for the column to read Threads, Outreach, Prospecting and
 * then Database & Infrastructure (2026-10-05): the kinds of work first, and
 * what they stand on last. Discovery is a plain group above Outreach since
 * 2026-10-26, so it lands in the first run with no change here.
 */
export const PROSPECTING_DRAWN_ABOVE = 'Database & Infrastructure'

/**
 * The groups in two runs, with the place for the Prospecting rows between them.
 *
 * `NAV_GROUPS` is not reordered for it, because the command palette, the rail
 * and the route checks all read that list, so the order is decided where the
 * groups are drawn: the open column and the rail both draw `above`, then the
 * Prospecting rows, then `below`.
 *
 * A split rather than a filter, so the groups keep the order of the list
 * whatever is added to it later. When the named group is not there to split
 * at, everything is above and the rows are drawn last.
 */
export function groupsAroundProspecting<Group extends { heading: string }>(
  groups: readonly Group[],
): { above: Group[]; below: Group[] } {
  const at = groups.findIndex((group) => group.heading === PROSPECTING_DRAWN_ABOVE)
  if (at === -1) return { above: [...groups], below: [] }
  return { above: groups.slice(0, at), below: groups.slice(at) }
}

// ---------------------------------------------------------------------------
// Operator: what was the Son of Basel portal, part of this app since 2026-10-30
// ---------------------------------------------------------------------------

/**
 * The Operator pages, at /operator (docs/SON-OF-BASEL-MERGE.md, task 3.4): the
 * bot's live view, the meetings it recorded, Ask, insights, Talk and the
 * clone. A top-level module beside Discovery, Outreach and Prospecting, built
 * the way the Prospecting block is: a separate list drawn under its own
 * heading, between the work groups and Database & Infrastructure, after the
 * Prospecting rows.
 *
 * Who sees them: admins always, and anybody GET /me on the Operator service
 * says has a seat (`useOperatorReach`). The service refuses everybody else by
 * itself, and the pages' own gate says so in a sentence, so hiding the rows
 * is tidiness, not the protection. Talk is the one row with a rule of its own
 * (`OPERATOR_TALK_HREF`): it speaks as the power admin's clone, so it is
 * offered to the power admin, and to anybody else only while the power
 * admin's timed demo is open, as the old portal's nav promised.
 *
 * NO COUNTS. The service is on another box, and a number on a row would make
 * every sidebar render wait on it.
 */
export type OperatorGlyph =
  | 'live'
  | 'meetings'
  | 'ask'
  | 'insights'
  | 'talk'
  | 'clone'
  | 'preferences'
  | 'onboarding'
  | 'sandbox'
  | 'settings'
  | 'companies'

export interface OperatorLink {
  href: string
  label: string
  glyph: OperatorGlyph
  hint: string
}

/** The heading over the rows, the module's name. */
export const OPERATOR_HEADING = 'Operator'

/*
 * SIX ROWS. Live first, because it is where the bot is joined to a meeting
 * and the rail's one glyph opens the first row. Settings is a tab of the
 * Settings page (`/settings/operator`), as Prospecting's is, so it is not a
 * row here. Preferences, onboarding and the admins' sandbox are offered by
 * name in the command palette (`OPERATOR_UNLISTED`). Talk is drawn only when
 * `OperatorOffer.talk` says so.
 */
export const OPERATOR_LINKS: readonly OperatorLink[] = [
  { href: '/operator/live', label: 'Live', glyph: 'live', hint: 'Send the bot into a meeting, and see who is there and what is said as it happens' },
  { href: '/operator/meetings', label: 'Meetings', glyph: 'meetings', hint: 'Every meeting the bot recorded, with its summary, transcript and recording' },
  { href: '/operator/ask', label: 'Ask', glyph: 'ask', hint: 'Ask a question across every meeting' },
  { href: '/operator/insights', label: 'Insights', glyph: 'insights', hint: 'What the week of meetings added up to' },
  { href: '/operator/talk', label: 'Talk', glyph: 'talk', hint: 'Talk to the clone out loud' },
  { href: '/operator/clone', label: 'Clone', glyph: 'clone', hint: 'The clone, its face and voice, and its Google sign-in' },
]

/** Talk's row, offered only to whom `OperatorOffer.talk` says. */
export const OPERATOR_TALK_HREF = '/operator/talk'

/**
 * What GET /me (and Talk's demo switch) say this person is offered, read by
 * `useOperatorReach` (pages/operator/shared). The same shape as its
 * `OperatorReach`, written out here so this file imports nothing.
 *
 *   seat   the rows at all
 *   admin  an Operator admin: the sandbox, Settings > Operator and its admin page
 *   talk   Talk: the power admin, or anybody with a seat while the demo is open
 */
export interface OperatorOffer {
  seat: boolean
  admin: boolean
  talk: boolean
}

/**
 * The Operator task engine's rows: Companies, what to do today for every
 * company (pages/operator/companies). ADMINS ONLY, unlike the rows above: the
 * pages need no seat in the meetings service, only the admin role, and the
 * API refuses anybody else. Drawn after the six rows in the Operator block.
 */
export const OPERATOR_TASK_LINKS: readonly OperatorLink[] = [
  { href: '/operator/companies', label: 'Companies', glyph: 'companies', hint: 'What to do today for every company, one click each' },
]

/** The task engine's rows this person sees: all of them for an admin, none for anybody else. */
export function operatorTaskLinksFor(isAdmin: boolean): readonly OperatorLink[] {
  return isAdmin ? OPERATOR_TASK_LINKS : []
}

/**
 * The Operator pages no row draws, offered by name in the command palette.
 * `adminOnly` keeps the sandbox and the admin page to Operator admins
 * (`OperatorOffer.admin`), as their screens are. `settings` puts a
 * destination in the settings group rather than under the module heading.
 */
export interface OperatorDestination extends OperatorLink {
  adminOnly?: boolean
  settings?: boolean
}

export const OPERATOR_UNLISTED: readonly OperatorDestination[] = [
  { href: '/operator/clone/meeting-signin', label: 'Meeting sign-in', glyph: 'clone', hint: 'Sign the clone in to Google so it can join meetings' },
  { href: '/operator/preferences', label: 'Preferences', glyph: 'preferences', hint: 'Your own Operator preferences' },
  { href: '/operator/onboarding', label: 'Onboarding', glyph: 'onboarding', hint: 'Set up the clone: identity, persona, photo, voice and calendar' },
  { href: '/operator/sandbox', label: 'Sandbox', glyph: 'sandbox', hint: 'The showcase library', adminOnly: true },
  {
    href: '/settings/operator/admin',
    label: 'Operator admin',
    glyph: 'settings',
    hint: 'Operator users, the credential pool and leads',
    adminOnly: true,
    settings: true,
  },
]

/** Is this row the page open now? Lit on its own page and on any page under it. */
export function operatorLinkLit(link: Pick<OperatorLink, 'href'>, pathname: string): boolean {
  const path = trimmed(pathname)
  return path === link.href || path.startsWith(`${link.href}/`)
}

/** Whether any Operator row is lit, for the folded rail's one glyph. Any Operator page lights it. */
export function anyOperatorLit(pathname: string): boolean {
  const path = trimmed(pathname)
  return path === '/operator' || path.startsWith('/operator/')
}

/**
 * The Operator rows this person sees: every row for an admin or anybody with
 * a seat, Talk only when it may be opened, and none for anybody else. The
 * offer comes from `useOperatorReach`, and an admin's rows never wait on it.
 */
export function operatorLinksFor(offer: Pick<OperatorOffer, 'seat' | 'talk'>): readonly OperatorLink[] {
  if (!offer.seat) return []
  return offer.talk ? OPERATOR_LINKS : OPERATOR_LINKS.filter((link) => link.href !== OPERATOR_TALK_HREF)
}

// ---------------------------------------------------------------------------
// The palette's face
// ---------------------------------------------------------------------------

/**
 * Cmd on an Apple keyboard, Ctrl everywhere else.
 *
 * Moved here from the top bar unchanged, including the reason it is careful:
 * printing the wrong one is worse than printing nothing, because somebody
 * presses what the label says, nothing happens, and the feature reads as broken
 * rather than as documented for a machine they are not sitting at.
 *
 * It takes the platform string instead of reading `navigator` so it can be
 * covered. The caller passes `navigator.platform || navigator.userAgent`, which
 * is deprecated and is still the only thing every browser answers here.
 */
export function modLabel(platform: string | null | undefined): string {
  if (!platform) return 'Ctrl '
  return /mac|iphone|ipad/i.test(platform) ? '⌘' : 'Ctrl '
}

// ---------------------------------------------------------------------------
// The person at the bottom
// ---------------------------------------------------------------------------

export interface AccountLines {
  /** The bold line. Never empty. */
  primary: string
  /** The grey line under it, or null when it would only repeat the line above. */
  secondary: string | null
}

/**
 * Two lines for the account band, and never the same line twice.
 *
 * The design draws a name over an address. Accounts created by the magic link
 * flow have no name until somebody fills one in, and a band showing an address
 * over the same address reads as a rendering fault. So the address is promoted
 * and the second line is dropped.
 */
export function accountLines(user: { name?: string | null; email?: string | null } | undefined): AccountLines | null {
  if (!user) return null
  const name = (user.name ?? '').trim()
  const email = (user.email ?? '').trim()
  if (name === '' && email === '') return null
  if (name === '' || name === email) return { primary: email, secondary: null }
  return { primary: name, secondary: email === '' ? null : email }
}

// ---------------------------------------------------------------------------
// Groups, and the hairlines between them
// ---------------------------------------------------------------------------

interface GroupLike<Item> {
  heading: string
  items: readonly Item[]
}

export interface RenderedGroup<Item> {
  heading: string
  items: Item[]
  /**
   * Draw a hairline above this group.
   *
   * The design divides the nav into bands with a rule between them, and the
   * rule belongs to the group BELOW it rather than the one above, so a group
   * that disappears takes no hairline with it and cannot leave a stray line
   * hanging at the bottom of the list. The first rendered group never has one.
   */
  dividerAbove: boolean
}

/**
 * Whether a navigation row is shown to this person.
 *
 * Two independent gates. `adminOnly` is the platform admin role. `superAdminView`
 * is the super admin with the switch on, the only person who sees every brand
 * at once: in the admin view a super admin sees exactly what any admin sees.
 * The server refuses the same screens on the same terms, so hiding a row here
 * is tidiness, not the protection.
 */
export function mayOpen(
  item: { adminOnly?: boolean; superAdminView?: boolean },
  isAdmin: boolean,
  superAdminView: boolean,
): boolean {
  if (item.adminOnly === true && !isAdmin) return false
  if (item.superAdminView === true && !superAdminView) return false
  return true
}

/**
 * The groups a given person actually sees, in order, with their dividers.
 *
 * Two things here are easy to get wrong by hand and both have been wrong in
 * navigation code before. A group whose every item is admin only must vanish
 * entirely rather than render an empty heading, and the divider has to count
 * groups that SURVIVED the filter rather than groups in the source list, or a
 * member sees a rule with nothing above it.
 */
export function visibleGroups<Item extends { adminOnly?: boolean; superAdminView?: boolean }>(
  groups: readonly GroupLike<Item>[],
  isAdmin: boolean,
  superAdminView = false,
): RenderedGroup<Item>[] {
  const out: RenderedGroup<Item>[] = []
  for (const group of groups) {
    const items = group.items.filter((item) => mayOpen(item, isAdmin, superAdminView))
    if (items.length === 0) continue
    out.push({
      heading: group.heading,
      items,
      dividerAbove: out.length > 0,
    })
  }
  return out
}

/*
 * `groupIsOpen` was here, and the fold it drove went with the Setup group on
 * 2026-09-23. Sixteen configuration screens are behind one Settings row now,
 * so no group in this navigation is more than five rows and none of them
 * folds. It is deleted rather than left exported and unused, because a
 * disclosure helper sitting in a model nobody calls is a standing invitation
 * to fold something else that does not need it.
 */

/**
 * Whether a path is inside one of these items.
 *
 * Nothing folds any more, which is what this used to decide. It is kept because
 * it is the one place the rule "the page itself, or anything under it, but not
 * a path that merely starts with the same letters" is written down, and both
 * `/settings/api` against `/settings/apikeys` and `/prospects` against
 * `/prospecting` are live pairs in this product.
 */
export function holdsPath(items: readonly { to: string }[], pathname: string): boolean {
  return items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))
}

/**
 * Whether a row matches its own path exactly, here.
 *
 * `end` keeps an index row dark on the pages beside it: Company lists is
 * `/discovery`, and Signals and Scoring live at `/discovery/signals` and
 * `/discovery/scoring`. But one list at `/discovery/lists/<id>` is reached
 * from Company lists and is still that row's page, so a row can name the
 * paths under it that keep it lit in `also`. Inside one of them the row
 * matches as a prefix again, which is what lights it.
 */
export function rowEnd(item: { end?: boolean; also?: readonly string[] }, pathname: string): boolean {
  if (item.end !== true) return false
  const also = (item.also ?? []).map((to) => ({ to }))
  return !holdsPath(also, pathname)
}

/**
 * Whether the Settings row at the foot of the column is the one lit.
 *
 * Every configuration screen lives under `/settings`, so the row stays lit
 * inside any of them. The exception is a settings screen that also has a row of
 * its own in the column, such as Device or the Activity log in Operations:
 * there the row named for the screen is the answer to "where am I", and
 * lighting Settings as well would put two rows on at once.
 */
export function settingsRowLit(pathname: string, ownRows: readonly { to: string }[]): boolean {
  if (!holdsPath([{ to: '/settings' }], pathname)) return false
  return !holdsPath(ownRows, pathname)
}

// ---------------------------------------------------------------------------
// The folded column, remembered per person
// ---------------------------------------------------------------------------

/** The key the column used before the choice was kept per person. */
export const LEGACY_COLLAPSED_KEY = 'gjb.sidebar.collapsed'

/**
 * Where the fold is remembered, one key per person.
 *
 * Per person rather than per machine because two people share the office Mac
 * and one of them works from the rail. With nobody known yet the old shared key
 * is the answer, which is also what an existing choice was saved under.
 */
export function collapsedStorageKey(userId: string | null | undefined): string {
  return userId ? `${LEGACY_COLLAPSED_KEY}.${userId}` : LEGACY_COLLAPSED_KEY
}

/**
 * A stored value read back as a boolean, and nothing else.
 *
 * Storage is somebody else's text. A value that is not literally true or false
 * falls back rather than being coerced, because `"false"` is truthy and a
 * coerced string would fold the column for somebody who unfolded it.
 */
export function readStoredFlag(raw: string | null, fallback: boolean): boolean {
  if (raw === 'true') return true
  if (raw === 'false') return false
  return fallback
}

// ---------------------------------------------------------------------------
// The sending allowance card
// ---------------------------------------------------------------------------

export interface AllowanceRow {
  capDaily: number
  usedToday: number
}

export interface AllowanceSummary {
  left: number
  cap: number
  used: number
}

/**
 * Today's sending across every action this person may take, or nothing.
 *
 * The numbers are the server's, read off `/me/limits`, and this only adds them
 * up. An action with no allowance today contributes nothing rather than a zero
 * cap, and a person with no allowance anywhere gets no card at all: a meter
 * that is empty because there is nothing to measure reads as a meter that is
 * full.
 */
export function sendingAllowance(rows: readonly AllowanceRow[] | undefined): AllowanceSummary | null {
  if (rows === undefined) return null
  let cap = 0
  let used = 0
  for (const row of rows) {
    if (!Number.isFinite(row.capDaily) || row.capDaily <= 0) continue
    cap += Math.floor(row.capDaily)
    used += Math.max(0, Math.min(Math.floor(Number.isFinite(row.usedToday) ? row.usedToday : 0), row.capDaily))
  }
  if (cap === 0) return null
  return { left: cap - used, cap, used }
}

// ---------------------------------------------------------------------------
// The view switcher
// ---------------------------------------------------------------------------

/**
 * What the view switcher says it is on, and which row carries the tick.
 *
 * Off is this brand, the console as any admin sees it. On is every brand at
 * once, and when the page under it is one of the platform screens that screen's
 * row is ticked so the menu says where you are as well as which view is on.
 */
export function viewSwitcherState(
  superAdminView: boolean,
  pathname: string,
  platformRows: readonly { to: string; label: string }[],
): { label: string; checked: string } {
  if (!superAdminView) return { label: VIEW_COPY.thisBrand, checked: 'brand' }
  const here = platformRows.find((row) => holdsPath([row], pathname))
  return here === undefined
    ? { label: VIEW_COPY.everyBrand, checked: 'none' }
    : { label: here.label, checked: here.to }
}

export const VIEW_COPY = {
  thisBrand: 'This brand',
  everyBrand: 'Every brand',
  workspace: 'Workspace',
  platform: 'Platform',
  menuLabel: 'Switch view',
} as const

export function thisBrandDescription(brandName: string | null | undefined): string {
  return brandName ? `Everything scoped to ${brandName}` : 'Everything scoped to the brand you are in'
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/**
 * Every sentence the chrome says in its own voice, in one place so the house
 * rules can be held to it. Labels that are one word are not here, they carry no
 * grammar to get wrong.
 */
export const SIDEBAR_COPY = [
  'Open navigation',
  'Close navigation',
  'Collapse the navigation',
  'Expand the navigation',
  'Switch workspace',
  'Sign out',
  'Your name and picture',
  'Set up an authenticator app',
  'Your account',
  'Everything on screen is scoped to this brand.',
  'Picking a brand re-scopes every list and chart. Each sending account you own carries its own limits and they hold across all brands, so switching never gives you more room.',
  'You are not a member of any brand yet. An admin can add you to one.',
  'Brand file needs fixing before this brand can launch anything',
  'Sending allowance',
  'sends left today, across every channel',
  'More for your account',
  'Everything scoped to the brand you are in',
] as const
